(() => {
  if (window.saUgcLightbox) return;
  const STATE_KEY = 'saUgcLightbox';
  const canHover = () => window.matchMedia?.('(hover: hover) and (pointer: fine)').matches === true;
  const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

  const bind = (gallery) => {
    const dialog = gallery.querySelector('[data-sa-ugc-lightbox]');
    if (!dialog || typeof dialog.showModal !== 'function') return;
    gallery.dataset.saUgcBound = 'true';
    // Avoid ancestor transforms/overflow; the dialog is full screen in the top layer.
    document.body.appendChild(dialog);
    const track = dialog.querySelector('[data-sa-ugc-track]');
    const slides = [...dialog.querySelectorAll('[data-sa-ugc-slide]')];
    const steps = [...dialog.querySelectorAll('[data-sa-ugc-step]')];
    const events = new AbortController();
    const options = { signal: events.signal };
    const html = document.documentElement;
    let index = 0;
    let opener = null;
    // Each open pushes its own history entry, so the back gesture closes the photo instead of leaving the page.
    let token = null;
    // A history.back() is on its way; a second one would leave the product page.
    let backPending = false;
    let closeTimer = 0;
    let scrollFrame = 0;

    const ownsHistory = () => token !== null && history.state?.[STATE_KEY] === token;
    const clamp = (value) => Math.max(0, Math.min(slides.length - 1, value));
    // Large files load only for the visible photo and its neighbours.
    const hydrate = (center) => {
      for (let i = center - 1; i <= center + 1; i++) {
        const image = slides[i]?.querySelector('img[data-src]');
        if (image && !image.getAttribute('src')) image.src = image.dataset.src;
      }
    };
    const setIndex = (next) => {
      index = clamp(next);
      hydrate(index);
      slides.forEach((slide, i) => slide.toggleAttribute('data-current', i === index));
      steps.forEach((step) => {
        step.disabled = clamp(index + Number(step.dataset.saUgcStep)) === index;
      });
    };
    const scrollToIndex = (next, smooth) => {
      const left = clamp(next) * track.clientWidth;
      if (smooth && !reducedMotion() && track.scrollTo) track.scrollTo({ left, behavior: 'smooth' });
      else track.scrollLeft = left;
      setIndex(next);
    };

    const open = (next, trigger) => {
      if (dialog.open || !slides.length) return;
      opener = trigger;
      setIndex(next);
      dialog.showModal();
      html.classList.add('sa-ugc-lightbox-open');
      scrollToIndex(next, false);
      try {
        token = `${Date.now()}-${Math.random()}`;
        const state = history.state && typeof history.state === 'object' ? history.state : {};
        history.pushState({ ...state, [STATE_KEY]: token }, '');
      } catch {
        token = null;
      }
      try {
        if (typeof window.clarity === 'function') window.clarity('event', 'ugc_photo_open');
      } catch {}
    };
    // X, dark-area tap and Esc go through history so no extra back entry is left behind.
    const requestClose = () => {
      if (!dialog.open || backPending) return;
      if (!ownsHistory()) return dialog.close();
      backPending = true;
      history.back();
      clearTimeout(closeTimer);
      closeTimer = setTimeout(() => {
        // popstate never arrived (unusual in-app browsers): close without a second back().
        if (!dialog.open) return;
        token = null;
        dialog.close();
      }, 600);
    };

    gallery.addEventListener('click', (event) => {
      const trigger = event.target.closest?.('[data-sa-ugc-open]');
      if (trigger) open(Number(trigger.dataset.saUgcOpen) || 0, trigger);
    }, options);
    dialog.querySelector('[data-sa-ugc-close]')?.addEventListener('click', requestClose, options);
    steps.forEach((step) => step.addEventListener('click', () => scrollToIndex(index + Number(step.dataset.saUgcStep), true), options));

    // Tap on the dark area (not the photo, caption or a button) closes; a swipe never fires click.
    const isBlank = (target) => !target.closest?.('img, figcaption, button');
    let blankDown = false;
    dialog.addEventListener('pointerdown', (event) => { blankDown = isBlank(event.target); }, options);
    dialog.addEventListener('click', (event) => {
      if (blankDown && isBlank(event.target)) requestClose();
      blankDown = false;
    }, options);

    dialog.addEventListener('keydown', (event) => {
      if (!canHover() || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return;
      event.preventDefault();
      scrollToIndex(index + (event.key === 'ArrowRight' ? 1 : -1), true);
    }, options);
    // Esc and Android's back gesture (CloseWatcher) arrive as cancel. Without a fresh tap the browser
    // makes it non-cancelable and closes the dialog itself; the close handler then drops our entry once.
    dialog.addEventListener('cancel', (event) => {
      if (!event.cancelable) return;
      event.preventDefault();
      requestClose();
    }, options);
    dialog.addEventListener('close', () => {
      clearTimeout(closeTimer);
      html.classList.remove('sa-ugc-lightbox-open');
      // Closed without our popstate and with no back() under way: drop the pushed entry.
      if (!backPending && ownsHistory()) history.back();
      backPending = false;
      token = null;
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    }, options);

    track.addEventListener('scroll', () => {
      if (scrollFrame) return;
      scrollFrame = requestAnimationFrame(() => {
        scrollFrame = 0;
        const width = track.clientWidth;
        const next = width ? clamp(Math.round(track.scrollLeft / width)) : index;
        if (next !== index) setIndex(next);
      });
    }, options);
    // Rotation changes the slide width; stay on the same photo.
    window.addEventListener('resize', () => {
      if (dialog.open) track.scrollLeft = index * track.clientWidth;
    }, options);
    window.addEventListener('popstate', () => {
      if (dialog.open && !ownsHistory()) dialog.close();
    }, options);
    window.addEventListener('pageshow', (event) => {
      if (event.persisted && dialog.open) dialog.close();
    }, options);
    document.addEventListener('shopify:section:unload', (event) => {
      if (!event.target.contains(gallery)) return;
      // Theme editor: never navigate the editor frame while tearing down. The close event is queued and
      // the listeners are gone by then, so undo the page lock here.
      token = null;
      clearTimeout(closeTimer);
      cancelAnimationFrame(scrollFrame);
      html.classList.remove('sa-ugc-lightbox-open');
      if (dialog.open) dialog.close();
      events.abort();
      dialog.remove();
    }, options);
  };

  const initialize = (root = document) => {
    root.querySelectorAll('[data-sa-ugc-gallery]').forEach((gallery) => {
      if (!gallery.dataset.saUgcBound) bind(gallery);
    });
  };
  window.saUgcLightbox = { initialize };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
